<template>
    <div>
        <ul
            v-if="photos.length"
            data-test="photo-strip"
            class="mt-2 flex flex-wrap gap-3"
        >
            <li
                v-for="photo in photos"
                :key="photo.key"
                data-test="photo"
                class="relative w-24"
            >
                <div
                    class="aspect-w-1 aspect-h-1 overflow-hidden rounded-md border border-gray-200 bg-gray-100"
                >
                    <img
                        :src="photo.previewUrl"
                        alt="The photo you added to this review"
                        class="h-full w-full object-cover"
                    />
                </div>

                <div
                    v-if="photo.status === 'uploading'"
                    class="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-200"
                >
                    <div
                        data-test="photo-progress"
                        class="h-1 bg-nutreance-green"
                        :style="{ width: `${photo.progress}%` }"
                    />
                </div>

                <p
                    v-else-if="photo.status === 'failed'"
                    data-test="photo-failed"
                    class="mt-1 text-xs text-red-600"
                >
                    {{ photo.message }}
                </p>

                <button
                    type="button"
                    data-test="remove-photo"
                    class="absolute -right-2 -top-2 rounded-full bg-white text-gray-400 shadow hover:text-gray-600 focus:outline-none"
                    @click="remove(photo)"
                >
                    <span class="sr-only">Remove this photo</span>
                    <XCircleIcon class="h-6 w-6" aria-hidden="true" />
                </button>
            </li>
        </ul>

        <p
            v-if="full"
            data-test="photo-limit"
            class="mt-2 text-sm text-gray-500"
        >
            You have uploaded the maximum number of photos.
        </p>

        <ReviewDialog
            ref="dialog"
            :open="webcamOpen"
            panel-class="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-4 text-left shadow-xl transition-all sm:p-6"
            @close="closeWebcam"
        >
            <div ref="frame" class="mx-auto" :style="frameCap">
                <div class="aspect-w-4 aspect-h-3" :style="frameShape">
                    <div>
                        <!-- playsinline is not optional: without it iOS Safari
                             takes the self-view fullscreen and the shutter
                             button disappears. The mirroring is the self-view
                             only — capture() draws the video frame, which the
                             CSS transform never touches. -->
                        <video
                            ref="webcamVideo"
                            data-test="webcam-preview"
                            autoplay
                            playsinline
                            muted
                            class="h-full w-full rounded-md bg-black object-contain"
                            style="transform: scaleX(-1)"
                            @loadedmetadata="theCameraIsReady"
                            @resize="fitTheFrameToTheMedia"
                        />
                    </div>
                </div>
            </div>

            <div class="mt-4 flex justify-center gap-3">
                <button
                    type="button"
                    data-test="close-webcam"
                    class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                    @click="closeWebcam"
                >
                    Cancel
                </button>
                <!-- Held until the stream reports its dimensions: before
                     loadedmetadata the video measures zero by zero, which is
                     exactly what capture() would draw. -->
                <button
                    type="button"
                    data-test="take-photo"
                    :disabled="!cameraReady"
                    class="inline-flex items-center rounded-md border border-transparent bg-nutreance-green px-4 py-2 text-sm font-medium text-white hover:bg-nutreance-green-darker focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    @click="capture"
                >
                    <CameraIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                    Capture
                </button>
            </div>
        </ReviewDialog>
    </div>
</template>

<script setup lang="ts">
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
} from "vue";
import { CameraIcon, XCircleIcon } from "@heroicons/vue/solid";
import {
    requestReviewPhotoUploadUrl,
    uploadReviewMedia,
} from "../../api/reviews";
import { useImageDownscale } from "../../functions/useImageDownscale";
import { useMediaFrame } from "../../functions/useMediaFrame";
import ReviewDialog from "./ReviewDialog.vue";

interface Photo {
    key: number;
    // Null until Cloudflare has the bytes; only a photo with an id is worth posting.
    id: string | null;
    previewUrl: string;
    // Set only for a preview this component created, so unmount revokes exactly those.
    objectUrl: string | null;
    progress: number;
    status: "uploading" | "ready" | "failed";
    message: string;
}

const props = withDefaults(
    defineProps<{
        accountHash?: string;
        csrf?: string;
        initialIds?: string[];
        maximum?: number;
        thumbnailVariant?: string;
    }>(),
    {
        accountHash: "",
        csrf: "",
        initialIds: () => [],
        maximum: 3,
        thumbnailVariant: "",
    },
);

const emit = defineEmits<{
    (event: "cameraAvailable", available: boolean): void;
    (event: "changed", ids: string[]): void;
    (event: "atCapacity", atCapacity: boolean): void;
    (event: "uploading", uploading: boolean): void;
}>();

const { downscale } = useImageDownscale();

const dialog = ref<InstanceType<typeof ReviewDialog> | null>(null);
const panel = computed<HTMLElement | null>(() => dialog.value?.panel ?? null);

const { frame, frameShape, frameCap, fitTheFrameToTheMedia, resetTheFrame } =
    useMediaFrame(panel);

const photos = ref<Photo[]>([]);
const webcamOpen = ref(false);
const webcamVideo = ref<HTMLVideoElement | null>(null);
const cameraReady = ref(false);
const openingCamera = ref(false);

let stream: MediaStream | null = null;
let nextKey = 0;

const full = computed(() => photos.value.length >= props.maximum);

/**
 * Tell the section which ids to post, whether anything is still uploading, and whether another
 * photo can be taken at all.
 *
 * The order of the ids is meaningful: the server stores it as each photo's `position`.
 */
function announce(): void {
    emit(
        "changed",
        photos.value
            .map((photo) => photo.id)
            .filter((id): id is string => id !== null),
    );
    emit(
        "uploading",
        photos.value.some((photo) => photo.status === "uploading"),
    );
    emit("atCapacity", full.value);
}

function remove(photo: Photo): void {
    releasePreview(photo);

    photos.value = photos.value.filter((candidate) => candidate !== photo);

    announce();
}

function releasePreview(photo: Photo): void {
    if (photo.objectUrl) {
        URL.revokeObjectURL(photo.objectUrl);
    }
}

/**
 * Add as many of these as the review can still carry.
 */
async function addFiles(files: File[]): Promise<void> {
    for (const file of files) {
        if (full.value) {
            break;
        }

        await accept(Promise.resolve(file));
    }
}

/**
 * Take a slot in the strip, then downscale what arrives in it and upload it.
 *
 * The slot is claimed synchronously and the source awaited afterwards, so Save is already disabled
 * before any of this runs — the webcam path only has its bytes a `toBlob` callback later, and a
 * submission landing in that gap would navigate away from a capture nothing was waiting on.
 *
 * The thumbnail is the local blob rather than the delivered image, so the customer never waits on
 * a network round trip to see their own photo. `reactive` and not a plain object: the mutations
 * below go through this reference rather than the array's proxy, so a raw object would render once
 * and then never redraw its progress bar.
 */
async function accept(source: Promise<Blob>): Promise<void> {
    const photo = reactive<Photo>({
        key: nextKey++,
        id: null,
        previewUrl: "",
        objectUrl: null,
        progress: 0,
        status: "uploading",
        message: "",
    });

    photos.value.push(photo);
    announce();

    try {
        const downscaled = await downscale(await source);

        photo.objectUrl = URL.createObjectURL(downscaled);
        photo.previewUrl = photo.objectUrl;

        const upload = await requestReviewPhotoUploadUrl(props.csrf);

        await uploadReviewMedia(
            upload.uploadUrl,
            downscaled,
            "review-photo.jpg",
            (percent) => {
                photo.progress = percent;
            },
        );

        photo.id = upload.id;
        photo.status = "ready";
    } catch (error) {
        photo.status = "failed";
        photo.message =
            error instanceof Error
                ? error.message
                : "The photo could not be uploaded.";
    }

    announce();
}

/**
 * Show the live self-view, if this browser and this customer allow one.
 *
 * A refusal is not an error: the control goes away and the picker remains, which is the whole
 * fallback. One request at a time, because a second while the permission prompt is up resolves into
 * a second stream and overwrites the reference to the first — whose camera light then stays on with
 * nothing able to reach its tracks.
 */
async function openWebcam(): Promise<void> {
    if (openingCamera.value || stream) {
        return;
    }

    openingCamera.value = true;

    try {
        stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user" },
            audio: false,
        });
    } catch {
        emit("cameraAvailable", false);

        return;
    } finally {
        openingCamera.value = false;
    }

    cameraReady.value = false;
    webcamOpen.value = true;

    await nextTick();

    if (!webcamVideo.value) {
        // The dialog never mounted its video, so nothing would ever stop the tracks.
        closeWebcam();

        return;
    }

    webcamVideo.value.srcObject = stream;
}

/**
 * Stop every track. The camera light must go out with the self-view, so every exit from it — the
 * close button, a capture, unmount — comes through here.
 */
function stopTheCamera(): void {
    stream?.getTracks().forEach((track) => track.stop());
    stream = null;

    if (webcamVideo.value) {
        webcamVideo.value.srcObject = null;
    }
}

function theCameraIsReady(event: Event): void {
    fitTheFrameToTheMedia(event);
    cameraReady.value = true;
}

function closeWebcam(): void {
    stopTheCamera();
    resetTheFrame();
    webcamOpen.value = false;
}

/**
 * Draw the current frame at the camera's own resolution and hand it to the upload pipeline.
 */
function capture(): void {
    const video = webcamVideo.value;

    if (!video) {
        closeWebcam();

        return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas
        .getContext("2d")
        ?.drawImage(video, 0, 0, canvas.width, canvas.height);

    closeWebcam();
    accept(frameFrom(canvas));
}

/**
 * The frame already drawn on the canvas, as a JPEG blob.
 */
function frameFrom(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) =>
                blob
                    ? resolve(blob)
                    : reject(
                          new Error(
                              "This browser could not process the photo.",
                          ),
                      ),
            "image/jpeg",
        );
    });
}

onMounted(() => {
    // A capability check and not a permission one: navigator.permissions.query({ name: "camera" })
    // throws at this project's Safari floor, and getUserMedia() is where the permission answer
    // arrives anyway.
    emit(
        "cameraAvailable",
        typeof navigator.mediaDevices?.getUserMedia === "function",
    );

    // Ids the form carried back through a bounced submission. Their local blobs went with the
    // redirect, so these thumbnails come from the delivered image instead.
    photos.value = props.initialIds.map((id) => ({
        key: nextKey++,
        id,
        previewUrl: `https://imagedelivery.net/${props.accountHash}/${id}/${props.thumbnailVariant}`,
        objectUrl: null,
        progress: 100,
        status: "ready",
        message: "",
    }));

    announce();
});

onBeforeUnmount(() => {
    stopTheCamera();

    photos.value.forEach(releasePreview);
});

defineExpose({ addFiles, openWebcam });
</script>
